import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { getTeamLogoUrl } from '../utils/logoUrl';
import { formatDate, shouldRevealLeaguePrediction } from '../utils/dateUtils';
import {
  getPredictionOutcome,
  getOutcomeCardClasses,
  getOutcomeAccentColor,
  getOutcomeLabel,
  isScoredOutcome,
} from '../utils/predictionOutcomeUtils';
import { formatPoints } from '../utils/scoreUtils';
import { renderKitPreview, getKitStyleById } from '../utils/kitUtils';
import EditLeagueModal from '../components/EditLeagueModal';
import ScoringExplained from '../components/ScoringExplained';
import LeagueChat from '../components/LeagueChat';
import PillTabs from '../components/PillTabs';
import { hasUnreadMessages, setLastSeenTimestamp } from '../utils/leagueChatStorage';

interface User {
  id: string;
  teamName: string;
  firstName?: string;
  lastName?: string;
  kitStyle?: string;
  kitPrimaryColour?: string;
  kitSecondaryColour?: string;
}

interface Season {
  id: string;
  name: string;
  gameType?: string;
  countryCode?: string;
  isActive?: boolean;
}

interface League {
  id: string;
  name: string;
  description: string;
  creator: User;
  season: Season;
  type: 'public' | 'private' | 'invite_only' | 'fanleague' | 'official';
  isActive: boolean;
  maxMembers: number;
  currentMembers: number;
  inviteCode?: string;
  groupname?: string;
  createdAt: string;
}

interface LeagueMember {
  id?: string;
  user: User;
  role: 'member' | 'admin' | 'creator';
  joinedAt: string;
  totalPoints: number;
  predictionCount: number;
  correctPredictions: number;
  exactScoreCount: number;
  bankerCount: number;
  bankerPoints: number;
  position?: number;
}

interface StandingsResponse {
  standings: LeagueMember[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  userStanding: (LeagueMember & { position: number }) | null;
  memberDirectory: { id: string; teamName: string }[];
}

const STANDINGS_PAGE_SIZE = 100;

interface MonthlyWinner {
  position: number;
  userId: string;
  teamName: string;
  totalPoints: number;
}

interface LeagueMonth {
  year: number;
  month: number;
  label: string;
  status: 'in_progress' | 'awaiting_results' | 'final' | 'not_awarded';
  winners: MonthlyWinner[] | null;
}

interface MonthlyOverviewResponse {
  awardsEnabled: boolean;
  months: LeagueMonth[];
}

// 'season' for the full-season table, otherwise "YYYY-M"
type StandingsPeriod = 'season' | `${number}-${number}`;

const PODIUM_MEDALS: Record<number, string> = { 1: '🏆', 2: '🥈', 3: '🥉' };

const SHORT_MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface MatchGroup {
  id: string;
  name: string;
}

interface Team {
  id: string;
  name: string;
  shortName?: string;
  logoUrl?: string;
}

interface Match {
  id: string;
  homeTeam: Team;
  awayTeam: Team;
  matchDate: string;
  venue: string;
  isCup: boolean;
  predictionsClose: string;
  status: string;
  actualHomeScore?: number;
  actualAwayScore?: number;
  matchGroup?: {
    id: string;
    name: string;
    matchWeek?: number;
  };
}

interface Prediction {
  id: string;
  user: User;
  match: {
    id: string;
    homeTeam: {
      id: string;
      name: string;
      code?: string;
      logoUrl?: string;
    };
    awayTeam: {
      id: string;
      name: string;
      code?: string;
      logoUrl?: string;
    };
    matchDate: string;
    predictionsClose: string;
    homeGoals?: number;
    awayGoals?: number;
  };
  matchGroup: MatchGroup;
  homeGoals: number | null;
  awayGoals: number | null;
  isBanker: boolean;
  points?: number;
  pointsBreakdown?: {
    exactScore: number;
    correctResult: number;
    bankerBonus: number;
  };
  isScored: boolean;
  submittedAt: string;
}

/** Dev only: add ?skeletonDelay=3000 to the URL, or set REACT_APP_DEV_SKELETON_DELAY in .env */
const getDevSkeletonDelayMs = (): number => {
  if (process.env.NODE_ENV !== 'development') return 0;

  const fromUrl = new URLSearchParams(window.location.search).get('skeletonDelay');
  if (fromUrl) return Math.max(0, Number(fromUrl) || 0);

  const fromEnv = process.env.REACT_APP_DEV_SKELETON_DELAY;
  if (fromEnv) return Math.max(0, Number(fromEnv) || 0);

  return 0;
};

const SkeletonBar: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
);

const STANDINGS_SKELETON_ROWS = 8;
const CHAT_SKELETON_MESSAGES = 4;

const LeagueChatSkeleton: React.FC = () => (
  <div className="card rounded-lg shadow bg-gray-50 dark:bg-gray-800/30 flex flex-col h-full min-h-[400px] md:min-h-[500px] md:max-h-[700px]">
    <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
      <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>League Chat</h2>
    </div>
    <div className="flex-1 overflow-hidden px-4 py-3 space-y-4 min-h-0">
      {Array.from({ length: CHAT_SKELETON_MESSAGES }).map((_, index) => (
        <div key={index} className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <SkeletonBar className="h-3.5 w-24" />
            <SkeletonBar className="h-3 w-12" />
          </div>
          <SkeletonBar className={`h-3.5 ${index % 2 === 0 ? 'w-full' : 'w-4/5'}`} />
          {index % 3 === 0 && <SkeletonBar className="h-3.5 w-2/3" />}
        </div>
      ))}
    </div>
    <div className="px-4 py-3 border-t flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
      <div className="flex gap-2">
        <SkeletonBar className="flex-1 h-10" />
        <SkeletonBar className="h-10 w-16" />
      </div>
    </div>
  </div>
);

const LeagueDetailSkeleton: React.FC = () => (
  <div className="min-h-screen">
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <Link to="/leagues" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
          ← Back to Leagues
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <SkeletonBar className="h-9 w-64 max-w-full mb-3" />
            <SkeletonBar className="h-5 w-96 max-w-full" />
          </div>
        </div>
      </div>

      <div className="card rounded-lg shadow p-6 mb-6 bg-gray-50 dark:bg-gray-800/30">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="text-center flex flex-col items-center gap-2">
              <SkeletonBar className="h-8 w-24" />
              <SkeletonBar className="h-4 w-20" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-3/4">
          <div className="card rounded-lg shadow bg-gray-50 dark:bg-gray-800/30">
            <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>League Standings</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{ borderColor: 'var(--border)' }}>
                <thead style={{ backgroundColor: 'rgba(156, 163, 175, 0.1)' }}>
                  <tr>
                    {['Position', 'Team', 'Total Points', 'Correct Results', 'Exact Scores', 'Total Predictions'].map((label) => (
                      <th
                        key={label}
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                  {Array.from({ length: STANDINGS_SKELETON_ROWS }).map((_, index) => (
                    <tr key={index} style={{ borderColor: 'var(--border)' }}>
                      <td className="pl-6 pr-1 py-4">
                        <SkeletonBar className="h-4 w-6" />
                      </td>
                      <td className="px-2 py-4">
                        <div className="flex items-center space-x-3">
                          <SkeletonBar className="h-8 w-8 rounded flex-shrink-0" />
                          <SkeletonBar className="h-4 w-32" />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <SkeletonBar className="h-4 w-10" />
                      </td>
                      <td className="px-6 py-4">
                        <SkeletonBar className="h-4 w-8" />
                      </td>
                      <td className="px-6 py-4">
                        <SkeletonBar className="h-4 w-8" />
                      </td>
                      <td className="px-6 py-4">
                        <SkeletonBar className="h-4 w-8" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="hidden md:block md:w-1/4">
          <LeagueChatSkeleton />
        </div>
      </div>

      <ScoringExplained className="mt-6" />
    </div>
  </div>
);

const LeagueDetail: React.FC = () => {
  const { id: leagueId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [league, setLeague] = useState<League | null>(null);
  const [members, setMembers] = useState<LeagueMember[]>([]);
  const [memberDirectory, setMemberDirectory] = useState<{ id: string; teamName: string }[]>([]);
  const [userStanding, setUserStanding] = useState<(LeagueMember & { position: number }) | null>(null);
  const [standingsPage, setStandingsPage] = useState(1);
  const [standingsTotal, setStandingsTotal] = useState(0);
  const [standingsTotalPages, setStandingsTotalPages] = useState(1);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [matchGroups, setMatchGroups] = useState<MatchGroup[]>([]);
  const [currentMatchGroup, setCurrentMatchGroup] = useState<MatchGroup | null>(null);
  const [selectedMatchGroupId, setSelectedMatchGroupId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [removingUser, setRemovingUser] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [upcomingMatches, setUpcomingMatches] = useState<Match[]>([]);
  const [upcomingMatchesLoading, setUpcomingMatchesLoading] = useState(false);
  const [matchPredictions, setMatchPredictions] = useState<Record<string, { homeGoals: number; awayGoals: number }>>({});
  const [mobileTab, setMobileTab] = useState<'standings' | 'chat'>('standings');
  const [hasUnreadChat, setHasUnreadChat] = useState(false);
  const [latestMessageAt, setLatestMessageAt] = useState<string | null>(null);
  const [monthlyOverview, setMonthlyOverview] = useState<MonthlyOverviewResponse | null>(null);
  const [standingsPeriod, setStandingsPeriod] = useState<StandingsPeriod>('season');
  // Read by fetchStandings so pagination keeps the selected period without
  // making fetchStandings (a dependency of the initial load effect) change.
  const standingsPeriodRef = useRef<StandingsPeriod>('season');

  const fetchStandings = useCallback(async (page: number) => {
    if (!leagueId) return;

    const period = standingsPeriodRef.current;
    const periodParams = period === 'season'
      ? {}
      : { year: Number(period.split('-')[0]), month: Number(period.split('-')[1]) };

    try {
      setStandingsLoading(true);
      const response = await apiClient.get<StandingsResponse>(`/leagues/${leagueId}/standings`, {
        params: { page, limit: STANDINGS_PAGE_SIZE, ...periodParams },
      });
      const data = response.data;
      setMembers(data.standings);
      setStandingsTotal(data.total);
      setStandingsPage(data.page);
      setStandingsTotalPages(data.totalPages);
      setUserStanding(data.userStanding);
      if (data.memberDirectory.length > 0) {
        setMemberDirectory(data.memberDirectory);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load standings');
    } finally {
      setStandingsLoading(false);
    }
  }, [leagueId]);

  const handleStandingsPeriodChange = (period: StandingsPeriod) => {
    standingsPeriodRef.current = period;
    setStandingsPeriod(period);
    fetchStandings(1);
  };

  useEffect(() => {
    if (!leagueId) return;

    standingsPeriodRef.current = 'season';
    setStandingsPeriod('season');
    setMonthlyOverview(null);

    apiClient.get<MonthlyOverviewResponse>(`/leagues/${leagueId}/monthly`)
      .then((response) => setMonthlyOverview(response.data))
      .catch((err) => console.error('Error fetching monthly standings overview:', err));
  }, [leagueId]);
  
  useEffect(() => {
    const fetchLeagueData = async () => {
      const fetchStartedAt = Date.now();

      try {
        setLoading(true);
        
        // Fetch league details
        const leagueResponse = await apiClient.get(`/leagues/${leagueId}`);
        setLeague(leagueResponse.data);
        
        await fetchStandings(1);
        
        // Fetch match groups for this league
        const matchGroupsResponse = await apiClient.get(`/leagues/${leagueId}/match-groups`);
        setMatchGroups(matchGroupsResponse.data);
        
        // Fetch current match group
        const currentMatchGroupResponse = await apiClient.get(`/leagues/${leagueId}/current-match-group`);
        setCurrentMatchGroup(currentMatchGroupResponse.data);
        setSelectedMatchGroupId(currentMatchGroupResponse.data.id);
        
        // Check if user can edit the league
        if (user) {
          try {
            const editPermissionsResponse = await apiClient.get(`/leagues/${leagueId}/can-edit`);
            setCanEdit(editPermissionsResponse.data.canEdit);
          } catch (err) {
            // If error checking permissions, assume no edit access
            setCanEdit(false);
          }
        }
        
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load league data');
      } finally {
        const minDelay = getDevSkeletonDelayMs();
        if (minDelay > 0) {
          const remaining = minDelay - (Date.now() - fetchStartedAt);
          if (remaining > 0) {
            await new Promise((resolve) => setTimeout(resolve, remaining));
          }
        }
        setLoading(false);
      }
    };

    if (leagueId) {
      fetchLeagueData();
    }
  }, [leagueId, user, fetchStandings]);

  // Separate effect to fetch predictions when match group selection changes
  useEffect(() => {
    const fetchPredictions = async () => {
      if (!leagueId || !selectedMatchGroupId) return;
      
      try {
        setPredictionsLoading(true);
        const predictionsResponse = await apiClient.get(`/leagues/${leagueId}/predictions?matchGroupId=${selectedMatchGroupId}`);
        setPredictions(predictionsResponse.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load predictions');
      } finally {
        setPredictionsLoading(false);
      }
    };

    fetchPredictions();
  }, [leagueId, selectedMatchGroupId]);

  // Fetch upcoming matches for current and next match week
  useEffect(() => {
    const fetchUpcomingMatches = async () => {
      if (!leagueId || !user) return;
      
      try {
        setUpcomingMatchesLoading(true);
        const response = await apiClient.get(`/leagues/${leagueId}/upcoming-matches`);
        const matches = response.data;
        setUpcomingMatches(matches);
        
        // Fetch user's predictions for these matches
        if (matches.length > 0 && user && league) {
          try {
            // Get the season ID from the league
            const seasonId = league.season.id;
            if (seasonId) {
              const predictionsResponse = await apiClient.get(`/predictions/season/${seasonId}`);
              const allPredictions = predictionsResponse.data;
              
              // Create a map of matchId -> prediction
              const predictionsMap: Record<string, { homeGoals: number; awayGoals: number }> = {};
              matches.forEach((match: Match) => {
                const prediction = allPredictions.find((p: any) => p.match?.id === match.id);
                if (prediction) {
                  predictionsMap[match.id] = {
                    homeGoals: prediction.homeGoals,
                    awayGoals: prediction.awayGoals
                  };
                }
              });
              
              setMatchPredictions(predictionsMap);
            }
          } catch (predErr) {
            console.error('Error fetching predictions:', predErr);
            // Don't fail if predictions can't be fetched
          }
        }
      } catch (err: any) {
        console.error('Error fetching upcoming matches:', err);
        // Don't set error state - this is optional data
      } finally {
        setUpcomingMatchesLoading(false);
      }
    };

    if (leagueId && user && league) {
      fetchUpcomingMatches();
    }
  }, [leagueId, user, league]);

  const isUserInLeague = (): boolean => {
    return userStanding !== null;
  };

  const canViewLeagueChat = (): boolean => {
    return isUserInLeague() || user?.role === 'admin';
  };

  const handleLatestMessageChange = (latestAt: string | null) => {
    setLatestMessageAt(latestAt);
    if (mobileTab !== 'chat' && latestAt) {
      setHasUnreadChat(hasUnreadMessages(leagueId!, latestAt));
    }
  };

  const handleMobileTabChange = (tab: 'standings' | 'chat') => {
    setMobileTab(tab);
    if (tab === 'chat' && latestMessageAt) {
      setLastSeenTimestamp(leagueId!, latestMessageAt);
      setHasUnreadChat(false);
    }
  };

  // Poll for new chat messages when viewing standings on mobile
  useEffect(() => {
    if (!leagueId || !canViewLeagueChat() || mobileTab === 'chat') return;

    const checkUnread = async () => {
      try {
        const response = await apiClient.get(`/leagues/${leagueId}/messages`, { params: { limit: 1 } });
        const messages = response.data.messages;
        if (messages.length > 0) {
          const latest = messages[0].createdAt;
          setLatestMessageAt(latest);
          setHasUnreadChat(hasUnreadMessages(leagueId, latest));
        }
      } catch {
        // Ignore polling errors
      }
    };

    checkUnread();
    const interval = setInterval(checkUnread, 240000);
    return () => clearInterval(interval);
  }, [leagueId, mobileTab, userStanding, user]);

  const getUserRole = (): string | null => {
    return userStanding?.role || null;
  };

  const handleLeagueUpdate = (updatedLeague: League) => {
    setLeague(updatedLeague);
  };

  const handleLeagueDelete = () => {
    if (window.confirm('Are you sure you want to delete this league? This action cannot be undone.')) {
      // Handle league deletion
      console.log('Deleting league:', league?.id);
    }
  };

  const handleRemoveUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from this league?`)) {
      return;
    }

    setRemovingUser(userId);
    try {
      await apiClient.delete(`/leagues/${league?.id}/members/${userId}`);
      await fetchStandings(standingsPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove user from league');
    } finally {
      setRemovingUser(null);
    }
  };

  // Members are already sorted by totalPoints from the backend
  const sortedMembers = members;

  const formatPosition = (position: number): string => {
    const j = position % 10;
    const k = position % 100;
    if (j === 1 && k !== 11) return `${position}st`;
    if (j === 2 && k !== 12) return `${position}nd`;
    if (j === 3 && k !== 13) return `${position}rd`;
    return `${position}th`;
  };

  const userPosition = userStanding?.position ?? null;

  const selectedMonth = standingsPeriod === 'season'
    ? null
    : monthlyOverview?.months.find((m) => `${m.year}-${m.month}` === standingsPeriod) ?? null;
  // Newest first, so the current month sits right after "All Season"
  const selectableMonths = monthlyOverview ? [...monthlyOverview.months].reverse() : [];
  const standingsTabs = [
    { id: 'season', label: 'All Season' },
    ...selectableMonths.map((m) => ({
      id: `${m.year}-${m.month}`,
      label: `${SHORT_MONTH_NAMES[m.month - 1]} ${m.year}`,
    })),
  ];

  const standingsRangeStart = standingsTotal === 0 ? 0 : (standingsPage - 1) * STANDINGS_PAGE_SIZE + 1;
  const standingsRangeEnd = Math.min(standingsPage * STANDINGS_PAGE_SIZE, standingsTotal);


  // Group predictions by match
  const predictionsByMatch = predictions.reduce((groups, prediction) => {
    const matchId = prediction.match.id;
    if (!groups[matchId]) {
      groups[matchId] = {
        match: prediction.match,
        matchGroup: prediction.matchGroup,
        predictions: []
      };
    }
    groups[matchId].predictions.push(prediction);
    return groups;
  }, {} as Record<string, { match: any; matchGroup: any; predictions: Prediction[] }>);

  // Sort matches by date and sort predictions within each match by points (highest first), then by user name
  const sortedMatches = Object.values(predictionsByMatch)
    .sort((a, b) => new Date(a.match.matchDate).getTime() - new Date(b.match.matchDate).getTime())
    .map((matchData, index) => ({
      ...matchData,
      uniqueKey: `${matchData.match.id}-${index}`, // Add unique key for React
      predictions: matchData.predictions.sort((a, b) => {
        // Sort by points descending, then by user name ascending
        if (a.points !== b.points) {
          return (b.points || 0) - (a.points || 0);
        }
        return a.user.teamName.localeCompare(b.user.teamName);
      })
    }));

  if (loading) {
    return <LeagueDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">League not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/leagues" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Leagues
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{league.name}</h1>
              <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>{league.description}</p>
            </div>
            <div className="text-right">
              {/* <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Created by {league.creator.teamName}
              </div> */}
             {/*  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {formatDate(league.createdAt)}
              </div> */}


              {canEdit && (
                <div className="mt-3 float-right">
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="btn-secondary px-4 py-2 rounded-md flex items-center"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="ml-2">Edit League</span>
                  </button>
                </div>
              )}

              
            </div>
          </div>
        </div>

        {/* League Info */}
        <div className="card rounded-lg shadow p-6 mb-6 bg-gray-50 dark:bg-gray-800/30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{league.season.name}</div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Competition</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--highlight)' }}>{league.currentMembers}</div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Members</div>
            </div>
            <div className="text-center">
              <div
                className="text-2xl font-bold"
                style={{
                  color:
                    userPosition !== null && userPosition <= 3
                      ? 'var(--highlight)'
                      : 'var(--text-primary)',
                }}
              >
                {userPosition !== null ? formatPosition(userPosition) : '—'}
              </div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Your Position</div>
            </div>
          </div>

          {league.inviteCode && (
            <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: 'rgba(156, 163, 175, 0.1)' }}>
              {/* <div className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Invite Code:</div> */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex items-center space-x-2 flex-wrap">
                  <code className="px-3 py-1 border rounded text-sm font-mono" style={{ 
                    backgroundColor: 'var(--surface)', 
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }}>
                    {league.inviteCode}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(league.inviteCode!);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="text-sm"
                    style={{ color: 'var(--highlight)' }}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Share the invite code with your friends to let them join the league.
                </span> 


                {'share' in navigator && (
                  <button
                    onClick={async () => {
                      try {
                        const shareText = `Join my league "${league.name}" on Predictor Pro! 🏆\n\nUse invite code: ${league.inviteCode}\n\n${league.description ? `\n${league.description}\n` : ''}Come compete in my league!`;
                        
                        await navigator.share({
                          title: `Join ${league.name} on Predictor Pro`,
                          text: shareText,
                        });
                      } catch (error: any) {
                        // User cancelled share or share failed - this is fine, just ignore
                        if (error.name !== 'AbortError') {
                          console.error('Error sharing:', error);
                        }
                      }
                    }}
                    className="btn-primary inline-flex items-center px-4 py-2 text-sm font-medium rounded-md"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Invite Friends
                  </button>
                )}


              </div>
            </div>
          )}
          
        </div>

        {/* Actions */}
        {isUserInLeague() ? (
         /*  <div className="mt-6 mb-6 flex justify-center">
            <Link
              to={`/seasons/${league.season.id}`}
              className="btn-primary inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md"
            >
              View Matches &amp; Make Predictions
            </Link>
          </div> */
          <></>
        ) : (
          <div className="mt-6 mb-6 flex justify-center">
            <button
              onClick={async () => {
                try {
                  await apiClient.post(`/leagues/${league.id}/join`);
                  
                  // Clear the league cache so the joined league appears immediately in the leagues list
                  sessionStorage.removeItem('leagues_data');
                  sessionStorage.removeItem('user_leagues_data');
                  sessionStorage.removeItem('leagues_cache_timestamp');
                  
                  window.location.reload(); 
                } catch (err: any) {
                  setError(err.response?.data?.message || 'Failed to join league');
                }
              }}
              className="btn-primary inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md"
            >
              Join League
            </button>
          </div>
        )}

        {/* Upcoming Matches Section */}
        {isUserInLeague() && upcomingMatches.length > 0 && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Upcoming Matches
              </h2>
              <Link 
                to={`/seasons/${league.season.id}`}
                className="text-sm font-medium"
                style={{ color: 'var(--highlight)' }}
              >
                View All Matches →
              </Link>
            </div>

            {upcomingMatchesLoading ? (
              <div className="text-center py-8">
                <div className="text-lg" style={{ color: 'var(--text-secondary)' }}>Loading upcoming matches...</div>
              </div>
            ) : (
              <div className="relative mb-8 md:-mx-6 md:px-6 md:overflow-hidden">
                <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 md:mx-0 md:px-0">
                  <div className="flex gap-4 pr-12 md:pr-16" style={{ minWidth: 'max-content' }}>
                    {upcomingMatches.map((match) => {
                      const now = new Date();
                      const matchDate = new Date(match.matchDate);
                      const predictionsClose = new Date(match.predictionsClose);
                      const canPredict = now < predictionsClose;
                      
                      return (
                        <Link
                          key={match.id}
                          to={`/predictions/${match.id}`}
                          className="card shadow p-4 hover:shadow-lg transition-shadow bg-gray-50 dark:bg-gray-800/50 flex-shrink-0 w-[60vw] md:w-[280px] md:min-w-[280px]"
                        >

                          {/* Match Date */}
                          <div className="text-xs opacity-70 mb-3" style={{ color: 'var(--text-primary)' }}>
                            {formatDate(match.matchDate)}
                          </div>

                          {/* Teams and Score */}
                          <div className="mb-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {match.homeTeam.logoUrl && (
                                  <img 
                                    src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                                    alt={`${match.homeTeam.name} logo`}
                                    className="w-5 h-5 object-contain flex-shrink-0"
                                  />
                                )}
                                <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                  {match.homeTeam.shortName || match.homeTeam.name}
                                </span>
                              </div>
                              <div className="text-sm font-medium mx-2" style={{ color: 'var(--text-secondary)' }}>
                                vs
                              </div>
                              <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                                <span className="text-sm font-medium truncate text-right" style={{ color: 'var(--text-primary)' }}>
                                  {match.awayTeam.shortName || match.awayTeam.name}
                                </span>
                                {match.awayTeam.logoUrl && (
                                  <img 
                                    src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                                    alt={`${match.awayTeam.name} logo`}
                                    className="w-5 h-5 object-contain flex-shrink-0"
                                  />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Prediction Status */}
                          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                            {matchPredictions[match.id] ? (
                              <div className="flex items-center justify-between">
                                <div className="text-xs" style={{ color: 'var(--text-primary)' }}>
                                  Your Prediction: <span className="font-semibold">{matchPredictions[match.id].homeGoals} - {matchPredictions[match.id].awayGoals}</span>
                                </div>
                                {canPredict && (
                                  <Link
                                    to={`/predictions/${match.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-xs underline"
                                    style={{ color: 'var(--highlight)' }}
                                  >
                                    Edit
                                  </Link>
                                )}
                              </div>
                            ) : canPredict ? (
                              <div className="text-xs font-medium text-center" style={{ color: 'var(--highlight)' }}>
                                Make Prediction →
                              </div>
                            ) : (
                              <div className="text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
                                Predictions Closed
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
                {/* Gradient fade overlay on the right side */}
                <div 
                  className="absolute top-0 bottom-0 pointer-events-none z-10 w-8 md:w-16 bg-gradient-to-l from-[#eff5f6] via-[#eff5f6]/70 to-transparent dark:from-[#0C0E12] dark:via-[#0C0E12]/70 dark:to-transparent -right-2 md:right-0"
                ></div>
              </div>
            )}
          </div>
        )}

        {/* League Standings + Chat */}
        {canViewLeagueChat() && (
          <div className="md:hidden flex border-b mb-4" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => handleMobileTabChange('standings')}
              className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
                mobileTab === 'standings'
                  ? 'border-highlight text-highlight'
                  : 'border-transparent'
              }`}
              style={{ color: mobileTab === 'standings' ? undefined : 'var(--text-secondary)' }}
            >
              Standings
            </button>
            <button
              onClick={() => handleMobileTabChange('chat')}
              className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors relative ${
                mobileTab === 'chat'
                  ? 'border-highlight text-highlight'
                  : 'border-transparent'
              }`}
              style={{ color: mobileTab === 'chat' ? undefined : 'var(--text-secondary)' }}
            >
              Chat
              {hasUnreadChat && (
                <span className="absolute top-2 ml-1 w-2 h-2 rounded-full bg-highlight" />
              )}
            </button>
          </div>
        )}

        <div className={`flex flex-col ${canViewLeagueChat() ? 'md:flex-row' : ''} gap-4`}>
          <div className={`${canViewLeagueChat() && mobileTab === 'chat' ? 'hidden md:block' : ''} ${canViewLeagueChat() ? 'md:w-3/4' : 'w-full'}`}>
        {/* Standings period tabs (same pattern as My Predictions) */}
        {selectableMonths.length > 0 && (
          <PillTabs
            className="mb-4"
            ariaLabel="Standings period"
            tabs={standingsTabs}
            selectedId={standingsPeriod}
            onSelect={(id) => handleStandingsPeriodChange(id as StandingsPeriod)}
          />
        )}

        {/* League Standings */}
        <div className="card rounded-lg shadow bg-gray-50 dark:bg-gray-800/30">
          <div className="px-6 py-4 border-b flex flex-wrap items-center justify-between gap-4" style={{ borderColor: 'var(--border)' }}>
            <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>League Standings</h2>
            {standingsTotal > STANDINGS_PAGE_SIZE && (
              <span className="text-sm whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                {standingsRangeStart}-{standingsRangeEnd} of {standingsTotal} teams
              </span>
            )}
          </div>

          {selectedMonth && (
            <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              {monthlyOverview?.awardsEnabled && selectedMonth.status === 'final' && selectedMonth.winners && selectedMonth.winners.length > 0 ? (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--text-secondary)' }}>
                    {selectedMonth.label} winners
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedMonth.winners.map((winner) => (
                      <Link
                        key={winner.userId}
                        to={`/users/${winner.userId}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:underline ${
                          winner.userId === user?.id ? 'bg-highlight/10' : ''
                        }`}
                        style={{ borderColor: winner.userId === user?.id ? 'var(--highlight)' : 'var(--border)', color: 'var(--text-primary)' }}
                      >
                        <span>{PODIUM_MEDALS[winner.position]}</span>
                        <span className="font-medium">{winner.teamName}</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{winner.totalPoints} pts</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {selectedMonth.status === 'in_progress' && (monthlyOverview?.awardsEnabled
                    ? `Points from matches played in ${selectedMonth.label}. The top 3 are crowned when the month ends.`
                    : `Points from matches played in ${selectedMonth.label}.`)}
                  {selectedMonth.status === 'awaiting_results' && `${selectedMonth.label} winners will be confirmed once all results are in.`}
                  {selectedMonth.status === 'not_awarded' && `Points from matches played in ${selectedMonth.label}.`}
                  {selectedMonth.status === 'final' && `Nobody scored points in ${selectedMonth.label}.`}
                </p>
              )}
            </div>
          )}
          
          <div className="overflow-x-auto relative">
            {standingsLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 z-10">
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading standings...</span>
              </div>
            )}
            <table className="min-w-full divide-y" style={{ borderColor: 'var(--border)' }}>
              <thead style={{ backgroundColor: 'rgba(156, 163, 175, 0.1)' }}>
                <tr>
                  <th className="pl-6 pr-1 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                    Position
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                    Total Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                    Correct Results
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                    Exact Scores
                  </th>
{/*                   <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                    Total Predictions
                  </th> */}
                  {(getUserRole() === 'creator' || getUserRole() === 'admin' || user?.role === 'admin') && (
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                {sortedMembers.map((member) => {
                  const position = member.position ?? 0;
                  return (
                  <tr key={member.user.id} 
                  className={`${
                      member.user.id === user?.id 
                        ? 'bg-highlight/10 border-l-4 border-highlight' 
                        : 'hover:bg-light-bg dark:hover:bg-dark-bg'
                    } transition-colors`} 
                  style={{ 
                    borderColor: 'var(--border)'
                  }}>
                    <td className="pl-6 pr-1 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      <div className="flex items-center">
                        <span style={{
                          color: position <= 3 ? 'var(--highlight)' : 'var(--text-primary)',
                        }}>
                          {position}
                        </span>
                        {position === 1 && <span className="ml-2 text-yellow-500">🏆</span>}
                        {position === 2 && <span className="ml-2 text-gray-400">🥈</span>}
                        {position === 3 && <span className="ml-2 text-orange-600">🥉</span>}
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {/* Kit Preview */}
                          {member.user.kitStyle && (
                            <div className="w-8 h-8 rounded flex items-center justify-center">
                              {(() => {
                                const kitStyle = getKitStyleById(member.user.kitStyle);
                                if (kitStyle) {
                                  return renderKitPreview(
                                    kitStyle,
                                    member.user.kitPrimaryColour || '#2563eb',
                                    member.user.kitSecondaryColour || '#ffffff',
                                    'small'
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          {/* User Info */}
                          <div>
                            <Link 
                              to={`/users/${member.user.id}`}
                              className="text-sm font-medium hover:underline" 
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {member.user.teamName}
                            </Link>
                            {(league?.type === 'invite_only' || getUserRole() === 'creator' || getUserRole() === 'admin' || user?.role === 'admin') && (
                              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {member.user.firstName} {member.user.lastName}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div>                        
                          {(member.user.id === user?.id ) && (
                            <span className="ml-2 px-2 py-1 text-xs bg-highlight text-black rounded-full">
                              You
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                      {member.totalPoints}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {member.correctPredictions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {member.exactScoreCount}
                    </td>
{/*                     <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {member.predictionCount}
                    </td> */}
                    {(getUserRole() === 'creator' || getUserRole() === 'admin' || user?.role === 'admin') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {member.user.id !== user?.id && (
                          <button
                            onClick={() => handleRemoveUser(member.user.id, member.user.teamName)}
                            disabled={removingUser === member.user.id}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-xs px-2 py-1 rounded border border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            {removingUser === member.user.id ? 'Removing...' : 'Remove'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {standingsTotalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between gap-4" style={{ borderColor: 'var(--border)' }}>
              <button
                onClick={() => fetchStandings(standingsPage - 1)}
                disabled={standingsPage <= 1 || standingsLoading}
                className="btn-secondary px-3 py-1.5 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Page {standingsPage} of {standingsTotalPages}
              </span>
              <button
                onClick={() => fetchStandings(standingsPage + 1)}
                disabled={standingsPage >= standingsTotalPages || standingsLoading}
                className="btn-secondary px-3 py-1.5 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
          </div>

          {canViewLeagueChat() && (
            <div className={`${mobileTab === 'standings' ? 'hidden md:block' : ''} md:w-1/4`}>
              <LeagueChat
                leagueId={leagueId!}
                members={memberDirectory.length > 0 ? memberDirectory : members.map(m => ({ id: m.user.id, teamName: m.user.teamName }))}
                canPost={!!user}
                canDeleteMessages={
                  getUserRole() === 'creator' || getUserRole() === 'admin' || user?.role === 'admin'
                }
                isVisible={mobileTab === 'chat'}
                currentUserId={user?.id}
                onLatestMessageChange={handleLatestMessageChange}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        {isUserInLeague() ? (
          <div className="mt-6 flex justify-center">
            <Link
              to={`/seasons/${league.season.id}`}
              className="btn-primary inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md"
            >
              View Matches &amp; Make Predictions
            </Link>
          </div>
        ) : (
          <div className="mt-6 flex justify-center">
            <button
              onClick={async () => {
                try {
                  await apiClient.post(`/leagues/${league.id}/join`);
                  
                  // Clear the league cache so the joined league appears immediately in the leagues list
                  sessionStorage.removeItem('leagues_data');
                  sessionStorage.removeItem('user_leagues_data');
                  sessionStorage.removeItem('leagues_cache_timestamp');
                  
                  window.location.reload(); 
                } catch (err: any) {
                  setError(err.response?.data?.message || 'Failed to join league');
                }
              }}
              className="btn-primary inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md"
            >
              Join League
            </button>
          </div>
        )}

        {/* Scoring Explained */}
        <ScoringExplained className="mt-6" />

        {/* League Predictions */}
        {matchGroups.length > 0 && (
          <div>
            <div className="card rounded-lg shadow mt-6 bg-gray-50 dark:bg-gray-800/30">
              <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>League Predictions</h2>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>View predictions made by league members</p>
                  </div>

                </div>

                <div className="flex items-center space-x-3 mt-4">
                    
                    <select
                      id="matchweek-select"
                      value={selectedMatchGroupId}
                      onChange={(e) => setSelectedMatchGroupId(e.target.value)}
                      className="px-4 py-2 rounded border font-bold max-sm:w-full"
                      style={{
                        backgroundColor: 'var(--surface)',
                        borderColor: 'var(--border)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      {matchGroups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                          {group.id === currentMatchGroup?.id && ' (Current)'}
                        </option>
                      ))}
                    </select>
                  </div>

              </div>

            </div>
            
            <div className="mt-4">
              {predictionsLoading ? (
                <div className="text-center py-8">
                  <div className="text-lg" style={{ color: 'var(--text-secondary)' }}>Loading predictions...</div>
                </div>
              ) : predictions.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-lg" style={{ color: 'var(--text-secondary)' }}>No predictions found for this matchweek</div>
                </div>
              ) : (
                <div className="space-y-6">
                  {sortedMatches.map(matchData => {
                    const match = matchData.match;
                    const matchPredictions = matchData.predictions;

                    // Check if match has finished - look for actual scores in the match data
                    const hasFinished = match.homeGoals !== null && match.homeGoals !== undefined && 
                                       match.awayGoals !== null && match.awayGoals !== undefined;
                    
                    return (
                      <div key={matchData.uniqueKey} className="rounded-lg bg-white dark:bg-gray-900/50" style={{ border: '2px solid var(--border)' }}>
                        {/* Match Header */}
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/30 rounded-t-lg border-b" style={{ borderColor: 'var(--border)' }}>
                          <div className="flex items-center justify-between">
                                                          <div className="flex-1">
                                <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                                  
                                  <div className="flex flex-col items-center sm:flex-row sm:justify-end sm:items-center gap-1 sm:gap-2">
                                    {match.homeTeam.logoUrl && (
                                      <img 
                                        src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                                        alt={`${match.homeTeam.name} logo`}
                                        className="w-6 h-6 sm:w-8 sm:h-8 object-contain sm:order-2"
                                      />
                                    )}
                                    <span className="font-bold text-sm sm:text-md md:text-lg text-center sm:text-right sm:order-1" style={{ color: 'var(--text-primary)' }}>
                                      {match.homeTeam.name}
                                    </span>
                                  </div>
                                  
                                  <div className="px-3 sm:px-6 text-center">
                                    {hasFinished ? (
                                      <div>
                                        <div className="text-lg sm:text-xl md:text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                                          {match.homeGoals} - {match.awayGoals}
                                        </div>
                                        <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                                          Final Score
                                        </div>
                                      </div>
                                    ) : (
                                      <div>
                                        <div className="text-sm sm:text-md md:text-lg font-medium" style={{ color: 'var(--text-secondary)' }}>
                                          vs
                                        </div>
                                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                          {formatDate(match.matchDate)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex flex-col items-center sm:flex-row sm:justify-start sm:items-center gap-1 sm:gap-2">
                                    {match.awayTeam.logoUrl && (
                                      <img 
                                        src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                                        alt={`${match.awayTeam.name} logo`}
                                        className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                                      />
                                    )}
                                    <span className="font-bold text-sm sm:text-md md:text-lg text-center sm:text-left" style={{ color: 'var(--text-primary)' }}>
                                      {match.awayTeam.name}
                                    </span>
                                  </div>
                                </div>
                              </div>
                          </div>
                        </div>

                        {/* Predictions Table */}
                        {matchPredictions.length > 0 && (
                          <div className="p-4">
                            <div className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
                              Member Predictions ({matchPredictions.length})
                            </div>
                            <div className="space-y-2">
                              {matchPredictions.map(prediction => {
                                const outcome = getPredictionOutcome(
                                  match.homeGoals,
                                  match.awayGoals,
                                  prediction.homeGoals !== null && prediction.awayGoals !== null
                                    ? { homeGoals: prediction.homeGoals, awayGoals: prediction.awayGoals }
                                    : null,
                                  !hasFinished
                                );
                                const scored = hasFinished && isScoredOutcome(outcome);
                                const accentClass = scored ? getOutcomeAccentColor(outcome) : '';
                                const outcomeLabel = scored ? getOutcomeLabel(outcome) : null;

                                const showPrediction =
                                  prediction.user.id === user?.id ||
                                  (shouldRevealLeaguePrediction(
                                    prediction.match.matchDate,
                                    prediction.match.predictionsClose
                                  ) &&
                                    prediction.homeGoals !== null &&
                                    prediction.awayGoals !== null);

                                const rowClass = hasFinished && isScoredOutcome(outcome)
                                  ? getOutcomeCardClasses(outcome, true)
                                  : 'bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700';

                                return (
                                  <div
                                    key={prediction.id}
                                    className={`p-3 rounded-lg flex flex-wrap items-center justify-between gap-x-3 gap-y-2 ${rowClass}`}
                                  >
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                                      <Link
                                        to={`/users/${prediction.user.id}`}
                                        className={`font-medium text-sm hover:underline truncate max-w-[10rem] sm:max-w-none ${scored ? accentClass : ''}`}
                                        style={!scored ? { color: 'var(--text-primary)' } : undefined}
                                      >
                                        {prediction.user.teamName}
                                      </Link>
                                      {prediction.user.id === user?.id && (
                                        <span className="text-xs bg-highlight text-black px-2 py-0.5 rounded-full shrink-0">You</span>
                                      )}

                                      {prediction.isBanker && (
                                        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full shrink-0 ${
                                          scored ? 'bg-black/10 dark:bg-black/20' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                        } ${scored ? accentClass : ''}`}>
                                          Banker
                                        </span>
                                      )}
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0 ml-auto">
                                      {showPrediction ? (
                                        <>
                                          <span className={`text-sm font-bold tabular-nums ${scored ? accentClass : 'text-gray-800 dark:text-gray-200'}`}>
                                            {prediction.homeGoals} - {prediction.awayGoals}
                                          </span>
                                          {scored && prediction.points !== undefined && outcomeLabel && (
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap bg-black/10 dark:bg-black/20 ${accentClass}`}>
                                              {outcomeLabel} · {formatPoints(prediction.points ?? 0)}
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                          Hidden
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit League Modal */}
        {league && (
          <EditLeagueModal
            league={league}
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onUpdate={handleLeagueUpdate}
            onDelete={handleLeagueDelete}
          />
        )}
      </div>
    </div>
  );
};

export default LeagueDetail;